import { AppMenuFlags, useMenuData } from '@komi-app/meta-creator'

import config from 'config'
import { CollaboratorProfile } from 'models/talent/talent-profile-collaborator.model'
import { useMemo } from 'react'
import { useTypedSelector } from 'redux/rootReducer'
import { selectCollaborator, selectCollaboratorList, selectIsCollaborator, selectUserData } from 'redux/User/selector'
import { ROLE, UserProfile } from 'redux/User/types'
import { userHasRole } from 'utils/user'
import { fullname } from './useNavUser'
import { profileAdminLink } from 'components/PreviewLayoutHeader/utils'
import { UserDetail } from '@komi-app/auth-sdk'
import { useIsOwner } from './user-hooks'
import { FLAGS, getFeatureValue, useFeatureIsOn, useFeatureValue } from '@komi-app/flags-sdk'
import { useHasSMS } from './user-hooks/useHasSMS'
import { useAudienceAndCampaignFeatureOn } from './useAudienceAndCampaignMobileBlock'
import { MenuItem, NavUser } from '@komi-app/creator-ui'

export const useMenu = () => {
  const collaborator = useTypedSelector(selectCollaborator)
  const user = useTypedSelector(selectUserData)
  const isCollaborator = useTypedSelector(selectIsCollaborator)
  const collaborationTalentProfiles = useTypedSelector(selectCollaboratorList)

  const isAdmin =
    userHasRole([user, collaborator], ROLE.Admin) ||
    userHasRole([user, collaborator], ROLE.Moderator) ||
    userHasRole([user, collaborator], ROLE.External)
  const isSelfSign = userHasRole([user, collaborator], ROLE.SelfSign)
  const isTopTalent =
    userHasRole([user, collaborator], ROLE.Talent)
  const isOwner = useIsOwner()

  const profiles = useMemo(() => {
    let talentProfile: UserProfile | undefined
    let connectedProfile: CollaboratorProfile[] | undefined

    if (isCollaborator) {
      talentProfile = collaborator?.talentProfile
      connectedProfile = collaborationTalentProfiles
    }

    if (user) {
      talentProfile = user.talentProfile
      connectedProfile = user.collaborationTalentProfiles
    }

    const profiles = connectedProfile?.map(({
      talentProfile
    }) => ({
      id: talentProfile.id,
      name: fullname(talentProfile),
      avatar: talentProfile?.avatar,
      domain: profileAdminLink(talentProfile.user?.username)
    }))

    return profiles || []
  }, [user, collaborator])

  return useMenuData({
    user: (user || {}) as UserDetail, // DIRTY...
    profiles,
    authorize: {
      isAdmin,
      isOwner,
      isSelfSign,
      isTopTalent
    },
    service: {
      domain: config.service.domain,
      env: config.service.env,
      name: config.service.name,
      version: config.service.version,
      protocol: `${location.protocol}//`,
      adminUrl: config.admin.url
    },
    flags: useAppMenuFlags()
  }) as [MenuItem[], NavUser]
}

export default useMenu

export function useAppMenuFlags(): AppMenuFlags {
  const useCreatorSPA = useFeatureIsOn(FLAGS.USE_CREATOR_SPA)
  const useFreemium = useFeatureIsOn(FLAGS.KOM_84_FREEMIUM)
  const useAmplify = useFeatureIsOn(FLAGS.AMPLIFY_CAMPAIGNS)
  const useBrandDealHub = useFeatureIsOn(FLAGS.BRAND_DEAL_HUB)
  const useAppAuth = useFeatureIsOn(FLAGS.USE_APP_AUTH)
  const useEDCM = useFeatureIsOn(FLAGS.FEAT_CAC_305_EDCM)
  const useCRM = useFeatureIsOn(FLAGS.DCA)
  // const useCRMSettings = useDesktopOnly(FLAGS.DCA) // TODO: screen size limit
  const useCRMSettings = true// useAudienceAndCampaignFeatureOn(FLAGS.DCA) // TODO: screen size limit
  const useCelloMenu = useFeatureIsOn(FLAGS.FEAT_CELLO_NAV_MENU)
  const useReferralProgram = useFeatureIsOn(FLAGS.FEAT_CELLO_REFERRAL)
  const useReferralButton = getFeatureValue(
    FLAGS.FEAT_SB_1794_NAV_REFERRAL_BUTTON_PROPS,
    {
      variant: 'default',
      label: 'Refer a Friend'
    }
  )
  const useMonetization = useFeatureIsOn(FLAGS.USE_MONETIZATION_PAGE)
  const useBetaSubscription = useFeatureIsOn(FLAGS.BETA_SUBSCRIPTION)
  const useDCMRestrictionForSSX = useFeatureIsOn(
    FLAGS.FEAT_SB_627_SSX_RESTRICTED_DCM
  )
  const useTrialDuration = useFeatureValue(FLAGS.TRIAL_DURATION, 30)
  const hideAnalytics = useFeatureIsOn(FLAGS.FEAT_HIDE_ANALYTICS_FOR_SOME_USERS)
  const useSMS = useHasSMS()

  const useSettings = useFeatureIsOn(FLAGS.USE_SETTINGS)
  const useMiniSite = useFeatureIsOn(FLAGS.USE_MINI_SITE)

  return {
    useAmplify,
    useBrandDealHub,
    useAppAuth,
    useEDCM,
    useCRM,
    useCRMSettings,
    useCelloMenu,
    useReferralProgram,
    useReferralButton,
    useMonetization,
    useFreemium,
    useCreatorSPA,
    useBetaSubscription,
    useDCMRestrictionForSSX,
    useTrialDuration,
    hideAnalytics,
    useSMS,
    useSettings,
    useMiniSite
  }
}